// CRM 2.0 · M12 Template Builder step 3 · QR: the pages of the real print (sample booking, 2 applicants) as
// thumbnails, a QR switch on each. Page 1 always has one. DC-211: every page with a mapped system field has a QR by
// default; Legal can switch one off. A page with no system field has none (switch greyed out). A switch is saved as
// qrOn on the section the page belongs to (the first one starting on it, else the one carried over from before),
// so the print decides again per booking when the pages move; switched off = qrOn false with its qrView kept (the
// server keeps it). A New page line added on the page (a page fix) counts as a section here and comes first on its
// page. Under a page with a QR: the system fields printed on it (what the scan shows). Click a page to see it large.
// Read only unless canEdit (draft, not open in Edit in Word).
import React, { useCallback, useEffect, useRef, useState } from "react";
import cls from "./Templates.module.css";
import { getTemplateSections, saveTemplateSections, getTemplatePageMap } from "../../Services/Templates";
import { errorTost } from "../../Services/Toast";

// pdf.js draws the pages (loaded only when this step opens)
let pdfjsLoad = null;
function loadPdfjs() {
  if (!pdfjsLoad) {
    pdfjsLoad = import("pdfjs-dist/legacy/build/pdf.mjs").then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerSrc = new URL("pdfjs-dist/legacy/build/pdf.worker.min.mjs", import.meta.url).toString();
      return pdfjs;
    });
  }
  return pdfjsLoad;
}

async function pageImage(pdf, pageNo, widthPx) {
  const page = await pdf.getPage(pageNo);
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({ scale: widthPx / base.width });
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(viewport.width);
  canvas.height = Math.round(viewport.height);
  await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
  return canvas.toDataURL("image/jpeg", 0.8);
}

function QrStep({ versionId, canEdit, lockedWhy, fields }) {
  const [view, setView] = useState(null); // { sections, qr }
  const [map, setMap] = useState(null); // { pages, s: { orderNo: page }, f: { page: [field keys] }, k: [QRs left out] }
  const [thumbs, setThumbs] = useState({});
  const [working, setWorking] = useState(false);
  const [big, setBig] = useState(null); // { pageNo, src }
  const pdfRef = useRef(null);
  const runRef = useRef(0);
  const redoRef = useRef(null);
  const labelOf = Object.fromEntries((fields || []).map((f) => [f.key, f.label]));

  const workOut = useCallback(async () => {
    const run = ++runRef.current;
    setWorking(true);
    try {
      const [pdfjs, r] = await Promise.all([loadPdfjs(), getTemplatePageMap(versionId, 2)]);
      const bytes = Uint8Array.from(atob(r.pdf), (c) => c.charCodeAt(0));
      const pdf = await pdfjs.getDocument({ data: bytes }).promise;
      if (run !== runRef.current) return pdf.destroy();
      if (pdfRef.current) pdfRef.current.destroy();
      pdfRef.current = pdf;
      setMap({ pages: pdf.numPages, s: (r.map && r.map.s) || {}, f: (r.map && r.map.f) || {}, k: (r.map && r.map.k) || [] });
      // the pages come in as they are drawn; the old pictures stay until then
      for (let n = 1; n <= pdf.numPages; n += 1) {
        const src = await pageImage(pdf, n, 220);
        if (run !== runRef.current) return null;
        setThumbs((t) => ({ ...t, [n]: src }));
      }
      setThumbs((t) => Object.fromEntries(Object.entries(t).filter(([n]) => Number(n) <= pdf.numPages)));
    } catch (e) {
      if (run === runRef.current) errorTost(e.message);
    } finally {
      if (run === runRef.current) setWorking(false);
    }
    return null;
  }, [versionId]);

  useEffect(() => {
    getTemplateSections(versionId).then(setView).catch((e) => errorTost(e.message));
    workOut();
    return () => {
      runRef.current += 1;
      clearTimeout(redoRef.current);
      if (pdfRef.current) pdfRef.current.destroy();
      pdfRef.current = null;
    };
  }, [versionId, workOut]);

  async function openBig(pageNo) {
    setBig({ pageNo, src: null });
    if (!pdfRef.current) return;
    try {
      const src = await pageImage(pdfRef.current, pageNo, 900);
      setBig((b) => (b && b.pageNo === pageNo ? { pageNo, src } : b));
    } catch (e) {
      errorTost(e.message);
    }
  }

  if (!view) return <p className={cls.hint}>Reading the sections…</p>;
  const { sections, qr } = view;
  // the section a page belongs to (same rule as the print, engine/qr.js sectionOfPage): the first one starting on it
  // (a New page line first), else the last one that started before it
  const order = (a, b) => (a.fromFix ? 0 : 1) - (b.fromFix ? 0 : 1) || a.orderNo - b.orderNo;
  const sectionOf = (pageNo) => {
    if (!map) return null;
    const placed = sections.filter((s) => map.s[s.orderNo]);
    const starting = placed.filter((s) => map.s[s.orderNo] === pageNo).sort(order);
    if (starting.length) return starting[0];
    return placed.filter((s) => map.s[s.orderNo] < pageNo).sort((a, b) => map.s[b.orderNo] - map.s[a.orderNo] || b.orderNo - a.orderNo)[0] || null;
  };
  const switchedOff = (s) => !!s && !s.qrOn && !!s.qrView;
  const hasFields = (pageNo) => ((map && map.f[pageNo]) || []).length > 0;
  const qrOnPage = (pageNo) => pageNo === 1 || (hasFields(pageNo) && !switchedOff(sectionOf(pageNo)));
  const lost = map ? sections.filter((s) => s.qrOn && !map.s[s.orderNo]) : [];

  async function toggle(pageNo, on) {
    const owner = sectionOf(pageNo);
    if (!owner) return;
    const changes = [{ docTemplateSectionId: owner.docTemplateSectionId, qrOn: on }];
    // shown at once; the server keeps the qrView when off (so it stays off)
    setView((v) => ({ ...v, sections: v.sections.map((s) => (s.docTemplateSectionId === owner.docTemplateSectionId ? { ...s, qrOn: on, qrView: s.qrView || "PARTIES_VALUE" } : s)) }));
    try {
      setView(await saveTemplateSections(versionId, changes));
      // the pictures show the QR after the print is made again (a few switches in a row: once)
      clearTimeout(redoRef.current);
      redoRef.current = setTimeout(workOut, 1200);
    } catch (e) {
      errorTost(e.message);
      getTemplateSections(versionId).then(setView).catch(() => {});
    }
  }

  const switchOf = (pageNo) => {
    if (!qr.onThisKind) return null;
    if (pageNo === 1) {
      return <label className={cls.chk} title="Page 1 always has a QR: the Document Desk tracks the copy with it"><input type="checkbox" checked disabled readOnly /> QR · always</label>;
    }
    const owner = sectionOf(pageNo);
    const why = !hasFields(pageNo) ? "No system fields on this page, so no QR"
      : !owner ? "This page is before the first section: its QR is always on. Add a New page line in step 2 to switch it."
        : `Saved with “${owner.title}”: its other pages switch with it, and the print follows it when the pages move`;
    return (
      <label className={cls.chk} title={why}>
        <input type="checkbox" checked={qrOnPage(pageNo)} disabled={!canEdit || !hasFields(pageNo) || !owner} aria-label={`QR on page ${pageNo}`}
          onChange={(e) => toggle(pageNo, e.target.checked)} /> QR
      </label>
    );
  };

  const fieldsOn = (pageNo) => ((map && map.f[pageNo]) || []).map((k) => labelOf[k] || k);

  return (
    <div>
      {lockedWhy && <div className={cls.blockBox} style={{ marginBottom: "1.6rem" }}>{lockedWhy}</div>}
      {!qr.onThisKind && <div className={cls.sugBox} style={{ marginTop: 0, marginBottom: "1.6rem" }}>This document kind has no QR.</div>}
      {qr.onThisKind && qr.marginIssues.length > 0 && (
        <div className={cls.blockBox} style={{ marginBottom: "1.6rem" }}>
          <b>QR:</b> {qr.marginIssues.join(" · ")}. The QR is never printed over text: fix the margin in Page setup (step 2).
        </div>
      )}
      {map && map.k.length > 0 && <div className={cls.blockBox} style={{ marginBottom: "1.6rem" }}><b>QR left out:</b> {map.k.join(" · ")}</div>}
      {lost.length > 0 && (
        <div className={cls.sugBox} style={{ marginTop: 0, marginBottom: "1.6rem" }}>
          QR saved with a section that is not in the print any more: {lost.map((s) => s.title).join(" · ")}.
        </div>
      )}
      <div className={cls.actionbar} style={{ marginBottom: "1.2rem" }}>
        <span className={cls.hint} style={{ margin: 0 }}>
          {map ? <>The print has <b>{map.pages}</b> pages (sample booking, 2 applicants).</> : null}
          {qr.onThisKind ? ` Every page with system fields gets a QR; switch one off if it is not needed. It prints on the ${qr.mirrored ? "outer" : "right"} edge, ${qr.sizeMm} mm, in the margin.` : ""}
        </span>
        <span className={cls.savedNote} aria-live="polite">{working ? "Working out pages…" : ""}</span>
      </div>
      {!map && working && <p className={cls.hint}>Working out pages… a long agreement takes about half a minute.</p>}
      {map && (
        <div className={cls.thumbGrid}>
          {Array.from({ length: map.pages }, (_, i) => i + 1).map((n) => (
            <div key={n} className={`${cls.thumb} ${qr.onThisKind && qrOnPage(n) ? cls.thumbQr : ""}`}>
              <button className={cls.thumbImg} onClick={() => openBig(n)} aria-label={`Open page ${n}`}>
                {thumbs[n] ? <img src={thumbs[n]} alt={`Page ${n}`} /> : <span className={cls.hint}>Working out pages…</span>}
              </button>
              <div className={cls.thumbFoot}>
                <b>Page {n}</b>
                {switchOf(n)}
              </div>
              {qr.onThisKind && (qrOnPage(n) || !hasFields(n)) && (
                <p className={cls.hint} style={{ margin: ".4rem 0 0" }}>
                  {fieldsOn(n).length ? `Scan shows: ${fieldsOn(n).join(", ")}` : n === 1 ? "Page 1: tracking QR only" : "No system fields on this page"}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      {big && (
        <div className={cls.mask} role="dialog" aria-modal="true" aria-label={`Page ${big.pageNo}`} onClick={() => setBig(null)}>
          <div className={cls.modal} style={{ maxWidth: "96rem", width: "auto" }} onClick={(e) => e.stopPropagation()}>
            <div className={cls.modalHead}><h2>Page {big.pageNo}</h2></div>
            <div className={cls.modalBody} style={{ maxHeight: "78vh", overflow: "auto", textAlign: "center" }}>
              {big.src ? <img src={big.src} alt={`Page ${big.pageNo}`} style={{ maxWidth: "100%" }} /> : <p className={cls.hint}>Working out pages…</p>}
            </div>
            <div className={cls.modalFoot}>
              <button className={cls.btn} disabled={big.pageNo <= 1} onClick={() => openBig(big.pageNo - 1)}>← Page {big.pageNo - 1}</button>
              <button className={cls.btn} disabled={!map || big.pageNo >= map.pages} onClick={() => openBig(big.pageNo + 1)}>Page {big.pageNo + 1} →</button>
              <button className={`${cls.btn} ${cls.primary}`} onClick={() => setBig(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QrStep;
